import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import { logout, selectUser } from '../store/slices/authSlice';
import { ThemeToggle } from '../components/ThemeToggle';
import LoadingSpinner from '../components/LoadingSpinner';
import apiClient from '../services/api';
import {
  UserDetailed,
  UsageLimits,
  UserRole,
  SubscriptionTier,
  UserRoleChangeRequest,
  UserSubscriptionChangeRequest
} from '../types/api';
import './UserDetails.css';

const UserDetails: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectUser);

  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<UserDetailed | null>(null);
  const [usageLimits, setUsageLimits] = useState<UsageLimits | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const isAdmin = await apiClient.verifyAdminAccess();
        if (!isAdmin) {
          navigate('/dashboard');
          return;
        }
        if (userId) {
          await loadUserDetails();
        }
      } catch (error) {
        console.error('Error checking admin access:', error);
        navigate('/dashboard');
      }
    };

    checkAdminAccess();
  }, [navigate, userId]);

  const loadUserDetails = async () => {
    if (!userId) return;

    try {
      setIsLoading(true);
      setError(null);

      const [userDetails, limits] = await Promise.all([
        apiClient.getAdminUserDetails(userId),
        apiClient.getUserUsageLimits(userId)
      ]);

      setUser(userDetails);
      setUsageLimits(limits);
    } catch (error: any) {
      console.error('Error loading user details:', error);
      setError('Failed to load user details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserAction = async (action: string) => {
    if (!userId) return;

    try {
      setActionLoading(action);
      setError(null);

      switch (action) {
        case 'activate':
          await apiClient.activateUser(userId);
          break;
        case 'deactivate':
          await apiClient.deactivateUser(userId);
          break;
      }

      await loadUserDetails();
    } catch (error: any) {
      console.error(`Error ${action} user:`, error);
      setError(`Failed to ${action} user`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRoleChange = async (newRole: UserRole) => {
    if (!userId) return;

    try {
      setActionLoading('role');
      setError(null);

      const request: UserRoleChangeRequest = { new_role: newRole };
      await apiClient.changeUserRole(userId, request);

      setShowRoleModal(false);
      await loadUserDetails();
    } catch (error: any) {
      console.error('Error changing user role:', error);
      setError('Failed to change user role');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubscriptionChange = async (planName: string) => {
    if (!userId) return;

    try {
      setActionLoading('subscription');
      setError(null);

      const request: UserSubscriptionChangeRequest = { plan_name: planName };
      await apiClient.changeUserSubscription(userId, request);

      setShowSubscriptionModal(false);
      await loadUserDetails();
    } catch (error: any) {
      console.error('Error changing user subscription:', error);
      setError('Failed to change user subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case 'super_admin': return '#dc2626';
      case 'admin': return '#ea580c';
      case 'user': return '#059669';
      default: return '#6b7280';
    }
  };

  const getSubscriptionColor = (tier: string) => {
    switch (tier) {
      case 'premium': return '#7c3aed';
      case 'free': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="user-details">
        <div className="loading-container">
          <LoadingSpinner size="lg" text="Loading user details..." />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-details">
        <div className="error-container">
          <h2>User not found</h2>
          <Link to="/admin/users" className="btn btn-primary">
            Back to Users
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="user-details">
      <header className="admin-header">
        <div className="header-content">
          <div>
            <h1>User Details</h1>
            <p>{user.full_name || user.username}</p>
          </div>
          <div className="header-actions">
            <ThemeToggle />
            <Link to="/admin/users" className="btn btn-outline">
              Back to Users
            </Link>
            <Link to="/admin" className="btn btn-outline">
              Admin Dashboard
            </Link>
            <button className="btn btn-secondary" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="user-details-main">
        {error && (
          <div className="alert alert-error">
            {error}
            <button
              className="alert-retry"
              onClick={loadUserDetails}
            >
              Retry
            </button>
          </div>
        )}

        <div className="details-grid">
          {/* User Information */}
          <section className="details-card">
            <div className="card-header">
              <h2>User Information</h2>
              <div className="user-status">
                <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <div className="card-content">
              <div className="info-grid">
                <div className="info-item">
                  <label>Email</label>
                  <span>{user.email}</span>
                </div>
                <div className="info-item">
                  <label>Username</label>
                  <span>{user.username}</span>
                </div>
                <div className="info-item">
                  <label>Full Name</label>
                  <span>{user.full_name || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <label>User ID</label>
                  <span className="user-id">{user.user_id}</span>
                </div>
                <div className="info-item">
                  <label>Role</label>
                  <span
                    className="role-badge"
                    style={{ backgroundColor: getRoleColor(user.user_role) }}
                  >
                    {user.user_role.replace('_', ' ')}
                  </span>
                </div>
                <div className="info-item">
                  <label>Member Since</label>
                  <span>{formatDate(user.created_at)}</span>
                </div>
                <div className="info-item">
                  <label>Days Active</label>
                  <span>{user.days_active} days</span>
                </div>
              </div>
            </div>
          </section>

          {/* Subscription & Limits */}
          <section className="details-card">
            <div className="card-header">
              <h2>Subscription & Limits</h2>
              <span
                className="subscription-badge"
                style={{ backgroundColor: getSubscriptionColor(user.subscription_tier) }}
              >
                {user.subscription_tier}
              </span>
            </div>
            <div className="card-content">
              {usageLimits && (
                <div className="limits-grid">
                  <div className="limit-item">
                    <div className="limit-header">
                      <label>Chapters</label>
                      <span className={`limit-status ${usageLimits.chapters.allowed ? 'allowed' : 'exceeded'}`}>
                        {usageLimits.chapters.allowed ? 'Within Limit' : 'Exceeded'}
                      </span>
                    </div>
                    <div className="limit-details">
                      <span>
                        {usageLimits.chapters.current} / {usageLimits.chapters.unlimited ? '∞' : usageLimits.chapters.limit}
                      </span>
                      {!usageLimits.chapters.unlimited && (
                        <div className="limit-progress">
                          <div
                            className="limit-progress-bar"
                            style={{
                              width: `${Math.min((usageLimits.chapters.current / usageLimits.chapters.limit) * 100, 100)}%`,
                              backgroundColor: usageLimits.chapters.allowed ? '#10b981' : '#ef4444'
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="limit-item">
                    <div className="limit-header">
                      <label>AI Usage</label>
                      <span className={`limit-status ${usageLimits.ai_usage.allowed ? 'allowed' : 'exceeded'}`}>
                        {usageLimits.ai_usage.allowed ? 'Within Limit' : 'Exceeded'}
                      </span>
                    </div>
                    <div className="limit-details">
                      <span>
                        {usageLimits.ai_usage.current} / {usageLimits.ai_usage.unlimited ? '∞' : usageLimits.ai_usage.limit}
                      </span>
                      {!usageLimits.ai_usage.unlimited && (
                        <div className="limit-progress">
                          <div
                            className="limit-progress-bar"
                            style={{
                              width: `${Math.min((usageLimits.ai_usage.current / usageLimits.ai_usage.limit) * 100, 100)}%`,
                              backgroundColor: usageLimits.ai_usage.allowed ? '#10b981' : '#ef4444'
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {user.subscription_expires_at && (
                <div className="subscription-expiry">
                  <label>Subscription Expires</label>
                  <span>{formatDate(user.subscription_expires_at)}</span>
                </div>
              )}
            </div>
          </section>

          {/* Content Statistics */}
          <section className="details-card">
            <div className="card-header">
              <h2>Content Statistics</h2>
            </div>
            <div className="card-content">
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-value">{user.memoir_count}</div>
                  <div className="stat-label">Memoirs</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{user.chapter_count}</div>
                  <div className="stat-label">Chapters</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{user.total_word_count.toLocaleString()}</div>
                  <div className="stat-label">Total Words</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{user.days_active}</div>
                  <div className="stat-label">Days Active</div>
                </div>
              </div>
            </div>
          </section>

          {/* Actions */}
          <section className="details-card">
            <div className="card-header">
              <h2>Admin Actions</h2>
            </div>
            <div className="card-content">
              <div className="actions-grid">
                <button
                  className="action-btn primary"
                  onClick={() => setShowRoleModal(true)}
                  disabled={!!actionLoading}
                >
                  {actionLoading === 'role' ? <LoadingSpinner size="sm" color="white" /> : 'Change Role'}
                </button>

                <button
                  className="action-btn primary"
                  onClick={() => setShowSubscriptionModal(true)}
                  disabled={!!actionLoading}
                >
                  {actionLoading === 'subscription' ? <LoadingSpinner size="sm" color="white" /> : 'Change Subscription'}
                </button>

                {user.is_active ? (
                  <button
                    className="action-btn danger"
                    onClick={() => handleUserAction('deactivate')}
                    disabled={!!actionLoading}
                  >
                    {actionLoading === 'deactivate' ? <LoadingSpinner size="sm" color="white" /> : 'Deactivate User'}
                  </button>
                ) : (
                  <button
                    className="action-btn success"
                    onClick={() => handleUserAction('activate')}
                    disabled={!!actionLoading}
                  >
                    {actionLoading === 'activate' ? <LoadingSpinner size="sm" color="white" /> : 'Activate User'}
                  </button>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Role Change Modal */}
      {showRoleModal && (
        <div className="modal-overlay" onClick={() => setShowRoleModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change User Role</h2>
              <button
                className="close-btn"
                onClick={() => setShowRoleModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <p>Change role for <strong>{user.email}</strong>:</p>
              <div className="role-options">
                {(['user', 'admin', 'super_admin'] as UserRole[]).map(role => (
                  <button
                    key={role}
                    className={`role-option ${user.user_role === role ? 'current' : ''}`}
                    onClick={() => handleRoleChange(role)}
                    disabled={!!actionLoading || user.user_role === role}
                  >
                    {role.replace('_', ' ')}
                    {user.user_role === role && ' (Current)'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Change Modal */}
      {showSubscriptionModal && (
        <div className="modal-overlay" onClick={() => setShowSubscriptionModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change Subscription</h2>
              <button
                className="close-btn"
                onClick={() => setShowSubscriptionModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <p>Change subscription for <strong>{user.email}</strong>:</p>
              <div className="subscription-options">
                {(['free', 'pro', 'premium'] as SubscriptionTier[]).map(tier => (
                  <button
                    key={tier}
                    className={`subscription-option ${user.subscription_tier === tier ? 'current' : ''}`}
                    onClick={() => handleSubscriptionChange(tier)}
                    disabled={!!actionLoading || user.subscription_tier === tier}
                  >
                    {tier.charAt(0).toUpperCase() + tier.slice(1)}
                    {user.subscription_tier === tier && ' (Current)'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetails;